import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { Send, Loader2, MessageSquare, ArrowLeft, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";

const initials = (s = "") => s.split(/\s+/).map((x) => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
const timeAgo = (iso) => {
    if (!iso) return "";
    const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${Math.round(s / 3600)}h`;
    return `${Math.round(s / 86400)}d`;
};

const Inbox = () => {
    const { user } = useAuth();
    const location = useLocation();
    const [threads, setThreads] = useState([]);
    const [active, setActive] = useState(null); // { thread, messages }
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const [loadingThreads, setLoadingThreads] = useState(true);
    const bottomRef = useRef(null);

    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const loadThreads = useCallback(async () => {
        try {
            setLoadingThreads(true);
            const { data } = await api.get("/messages/threads");
            setThreads(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoadingThreads(false); }
    }, []);

    const openThread = useCallback(async (id) => {
        try {
            const { data } = await api.get(`/messages/threads/${id}`);
            setActive(data);
            setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 40);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);

    useEffect(() => { if (user) loadThreads(); }, [user, loadThreads]);

    useEffect(() => {
        const t = params.get("thread");
        if (t) openThread(t);
    }, [params, openThread]);

    const send = async (e) => {
        e.preventDefault();
        if (!active || !body.trim()) return;
        try {
            setSending(true);
            await api.post("/messages", { thread_id: active.thread.id, body });
            setBody("");
            await openThread(active.thread.id);
            loadThreads();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSending(false); }
    };

    if (!user) {
        return (
            <div className="container-page py-24 text-center">
                <MessageSquare size={26} className="mx-auto text-emerald-500" />
                <h1 className="mt-4 font-heading text-2xl font-medium">Please log in to view your inbox.</h1>
                <Link to="/login" className="btn-green mt-6 inline-flex">Log in</Link>
            </div>
        );
    }

    return (
        <div className="container-page py-12" data-testid="inbox-page">
            <div className="mb-8">
                <p className="overline">Messages</p>
                <h1 className="mt-2 font-heading font-medium text-3xl lg:text-4xl tracking-tight text-foreground">Inbox</h1>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-5 min-h-[520px]">
                {/* Thread list */}
                <aside className={`card-soft p-0 overflow-hidden ${active ? "hidden md:block" : ""}`}>
                    <div className="px-4 py-3 border-b border-border">
                        <Input placeholder="Search…" className="h-9 text-sm" data-testid="inbox-search" />
                    </div>
                    <div className="divide-y divide-border max-h-[520px] overflow-y-auto">
                        {loadingThreads ? (
                            <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={14} className="animate-spin" /> Loading…</div>
                        ) : threads.length === 0 ? (
                            <div className="p-8 text-center text-sm text-muted-foreground">
                                <Sparkles size={22} className="mx-auto text-emerald-500 mb-2" />
                                No conversations yet.
                            </div>
                        ) : threads.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => openThread(t.id)}
                                data-testid={`thread-${t.id}`}
                                className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-muted transition-colors ${active?.thread?.id === t.id ? "bg-emerald-500/5" : ""}`}
                            >
                                <Avatar className="w-9 h-9 shrink-0">
                                    <AvatarFallback className="bg-brand-navy text-white text-xs font-bold">{initials(t.other?.name || t.other?.email || "")}</AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                    <div className="flex justify-between gap-2 items-center">
                                        <div className="font-semibold text-sm text-foreground truncate">{t.other?.name || t.other?.email || "Unknown"}</div>
                                        <div className="text-[10px] text-muted-foreground shrink-0">{timeAgo(t.last_message_at)}</div>
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate">{t.last_preview || "—"}</div>
                                    <div className="flex items-center gap-1.5 mt-1">
                                        {t.other?.role && <Badge variant="outline" className="text-[10px] py-0">{t.other.role}</Badge>}
                                        {t.unread_count > 0 && <span className="rounded-full bg-brand-green text-white text-[10px] font-bold px-1.5">{t.unread_count}</span>}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </aside>

                {/* Active thread */}
                <section className={`card-soft flex flex-col ${!active ? "hidden md:flex" : ""}`}>
                    {!active ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-12 text-center text-muted-foreground">
                            <MessageSquare size={26} className="text-emerald-500" />
                            <p className="mt-3 text-sm">Select a conversation to start chatting.</p>
                        </div>
                    ) : (
                        <>
                            <header className="px-5 py-4 border-b border-border flex items-center gap-3">
                                <button onClick={() => setActive(null)} className="md:hidden text-muted-foreground"><ArrowLeft size={18} /></button>
                                <Avatar className="w-9 h-9">
                                    <AvatarFallback className="bg-brand-navy text-white text-xs font-bold">{initials(active.thread.other?.name || active.thread.other?.email || "")}</AvatarFallback>
                                </Avatar>
                                <div>
                                    <div className="font-semibold text-foreground">{active.thread.other?.name || "Unknown"}</div>
                                    <div className="text-xs text-muted-foreground">{active.thread.other?.email}</div>
                                </div>
                            </header>
                            <div className="flex-1 overflow-y-auto px-5 py-6 space-y-3 max-h-[420px]" data-testid="thread-messages">
                                {(active.messages || []).map((m) => {
                                    const mine = m.from_user_id === user.user_id;
                                    return (
                                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                            <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-brand-green text-white rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm"}`}>
                                                {m.body}
                                                <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>{timeAgo(m.created_at)}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={bottomRef} />
                            </div>
                            <form onSubmit={send} className="border-t border-border p-3 flex gap-2 items-end">
                                <Textarea
                                    rows={1}
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    placeholder="Type a message…"
                                    data-testid="message-input"
                                    className="resize-none min-h-[42px] max-h-32"
                                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
                                />
                                <button
                                    type="submit"
                                    disabled={sending || !body.trim()}
                                    data-testid="message-send"
                                    className="btn-green shrink-0 disabled:opacity-50"
                                >
                                    {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                                </button>
                            </form>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
};

export default Inbox;
